import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useContactServiceAdapter, useTranslations } from './providers/ContactServiceProvider'
import { useContactStore } from './store/useContactStore'
import { ContactGrid } from './parts/ContactGrid'
import { ContactModal, type ContactModalMode, type ContactModalFormData } from './parts/ContactModal'
import { DeleteDefaultConfirmationDialog } from './parts/DeleteDefaultConfirmationDialog'
import type { ContactManagerProps } from './types/component.types'
import type { Contact } from './types/contact.types'
import type { CatalogItem } from './types/adapter.types'
import { DEFAULT_VALUE, buildEnumOptions } from './types/enum.types'

interface ModalState {
  isOpen: boolean
  mode: ContactModalMode
  contact: Contact | null
}

export function ContactManager({
  recordId,
  mode = 'edit',
  useForOptions: useForOptionsProp,
  phoneCategoryOptions,
  onContactsChange,
}: ContactManagerProps) {
  const adapter = useContactServiceAdapter()
  const t = useTranslations()

  // Determine if consumer extended enum labels via translations
  const consumerExtendedUseFor = Object.keys(t.enums.useFor).length > 1
  const consumerExtendedPhoneCat = Object.keys(t.enums.phoneCategory).length > 1

  // Fetch classification values from backend (only when no prop AND no translation extension)
  const [fetchedValues, setFetchedValues] = useState<number[] | null>(null)

  useEffect(() => {
    if (useForOptionsProp || consumerExtendedUseFor || !adapter.getClassifications) return
    let cancelled = false
    adapter.getClassifications().then((result) => {
      if (!cancelled) setFetchedValues(result)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [adapter, useForOptionsProp, consumerExtendedUseFor])

  // UseFor resolution: prop > translation-extended > backend-fetched > fallback
  const resolvedOptions = useForOptionsProp
    ?? (consumerExtendedUseFor ? buildEnumOptions(t.enums.useFor) : undefined)
    ?? fetchedValues?.map((v) => ({
      value: v,
      label: t.enums.useFor[v] ?? String(v),
    }))
    ?? buildEnumOptions(t.enums.useFor)

  // PhoneCategory resolution: prop > translation-extended > fallback
  const resolvedPhoneCategoryOptions = phoneCategoryOptions
    ?? (consumerExtendedPhoneCat ? buildEnumOptions(t.enums.phoneCategory) : undefined)
    ?? buildEnumOptions(t.enums.phoneCategory)

  // AC3: Inject default classification if not present in consumer options
  const normalizedUseForOptions = useMemo(() => {
    return resolvedOptions.some((o) => o.value === DEFAULT_VALUE)
      ? resolvedOptions
      : [{ value: DEFAULT_VALUE, label: t.enums.useFor[0] ?? t.manager.defaultLabel }, ...resolvedOptions]
  }, [resolvedOptions, t.enums.useFor, t.manager.defaultLabel])
  // Social network types — loaded once via fetcher for grid label resolution
  const [socialNetworkTypes, setSocialNetworkTypes] = useState<CatalogItem[]>([])

  useEffect(() => {
    let cancelled = false
    const snConfig = adapter.lookupConfig.socialNetworkTypes
    adapter.lookupConfig.fetcher(snConfig.entity, { fields: ['Id', ...snConfig.displayFields], page: 1, pageSize: 500 })
      .then((res) => {
        if (!cancelled) {
          setSocialNetworkTypes(
            res.data.map((r: Record<string, unknown>) => ({
              id: String(r['Id'] ?? r['id'] ?? ''),
              name: snConfig.displayFields.map((f) => String(r[f] ?? '')).filter(Boolean).join(' - '),
            })),
          )
        }
      }).catch(() => {})
    return () => { cancelled = true }
  }, [adapter])

  const loadContacts = useContactStore((s) => s.loadContacts)
  const contacts = useContactStore((s) => s.contacts)
  const pendingAdds = useContactStore((s) => s.pendingAdds)
  const pendingEdits = useContactStore((s) => s.pendingEdits)
  const pendingDeletes = useContactStore((s) => s.pendingDeletes)
  const addContact = useContactStore((s) => s.addContact)
  const editContact = useContactStore((s) => s.editContact)
  const deleteContact = useContactStore((s) => s.deleteContact)
  const restoreContact = useContactStore((s) => s.restoreContact)
  const [isLoading, setIsLoading] = useState(true)

  // Delete default confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    mode: 'create',
    contact: null,
  })

  const modalStateRef = useRef(modalState)
  modalStateRef.current = modalState

  // Notify parent of pending changes
  useEffect(() => {
    const hasChanges = pendingAdds.length > 0 || pendingEdits.size > 0 || pendingDeletes.size > 0
    onContactsChange?.(hasChanges)
  }, [pendingAdds, pendingEdits, pendingDeletes, onContactsChange])

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    adapter.getByRecordId(recordId).then((loaded) => {
      if (!cancelled) {
        loadContacts(recordId, loaded)
        setIsLoading(false)
      }
    }).catch(() => {
      if (!cancelled) {
        setIsLoading(false)
      }
    })
    return () => { cancelled = true }
  }, [recordId, adapter, loadContacts])

  const handleAdd = useCallback(() => {
    setModalState({ isOpen: true, mode: 'create', contact: null })
  }, [])

  const handleEdit = useCallback((id: string) => {
    const found = contacts.find((c) => c.id === id) ?? null
    if (found) {
      setModalState({ isOpen: true, mode: 'edit', contact: found })
    }
  }, [contacts])

  const handleView = useCallback((id: string) => {
    const found = contacts.find((c) => c.id === id) ?? null
    if (found) {
      setModalState({ isOpen: true, mode: 'view', contact: found })
    }
  }, [contacts])

  const handleClose = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  // Compute if next created contact would be the first
  const isFirstContact = useMemo(() => {
    const activeContacts = contacts.filter((c) => !pendingDeletes.has(c.id ?? ''))
    return activeContacts.length === 0
  }, [contacts, pendingDeletes])

  // AC1/AC2: Compute current default contact for transfer detection
  const currentDefaultContact = useMemo(
    () => contacts.find((c) => c.useFor.includes(DEFAULT_VALUE) && !pendingDeletes.has(c.id ?? '')) ?? null,
    [contacts, pendingDeletes],
  )

  // AC: current default would be left with empty useFor if we transfer
  const currentDefaultOnlyHasDefault = useMemo(() => {
    if (!currentDefaultContact) return false
    return currentDefaultContact.useFor.length === 1 && currentDefaultContact.useFor[0] === DEFAULT_VALUE
  }, [currentDefaultContact])

  const isOnlyDefault = useMemo(() => {
    if (!modalState.contact) return false
    return modalState.contact.useFor.includes(DEFAULT_VALUE)
      && contacts.filter((c) => c.useFor.includes(DEFAULT_VALUE) && !pendingDeletes.has(c.id ?? '')).length === 1
  }, [modalState.contact, contacts, pendingDeletes])

  const handleSave = useCallback((data: ContactModalFormData) => {
    const { mode: currentMode, contact: currentContact } = modalStateRef.current
    const useFor = data.useFor ?? []

    if (currentMode === 'create') {
      // AC1: Auto-ensure default on first contact
      const activeContacts = contacts.filter((c) => !pendingDeletes.has(c.id ?? ''))
      const ensuredUseFor = activeContacts.length === 0 && !useFor.includes(DEFAULT_VALUE)
        ? [DEFAULT_VALUE, ...useFor]
        : useFor

      addContact({
        recordId,
        name: data.name,
        emails: data.emails,
        address: data.address ?? null,
        description: data.description ?? null,
        cityId: data.cityId ?? null,
        neighborhoodId: data.neighborhoodId ?? null,
        useFor: ensuredUseFor,
        phones: data.phones ?? [],
        socialNetworks: data.socialNetworks ?? [],
        isDefault: ensuredUseFor.includes(DEFAULT_VALUE),
      })

      // AC2: Transfer — remove default from old default if new contact has it
      if (ensuredUseFor.includes(DEFAULT_VALUE)) {
        const oldDefault = contacts.find((c) => c.useFor.includes(DEFAULT_VALUE) && !pendingDeletes.has(c.id ?? ''))
        if (oldDefault?.id) {
          const newUseFor = oldDefault.useFor.filter((u) => u !== DEFAULT_VALUE)
          // Safety: never leave old default with empty classification
          if (newUseFor.length > 0) {
            editContact(oldDefault.id, {
              useFor: newUseFor,
              isDefault: false,
            })
          }
        }
      }
    } else if (currentMode === 'edit' && currentContact?.id) {
      // AC2: Transfer — remove default from old default if transfer happened
      if (useFor.includes(DEFAULT_VALUE) && currentDefaultContact && currentDefaultContact.id !== currentContact.id) {
        const newUseFor = currentDefaultContact.useFor.filter((u) => u !== DEFAULT_VALUE)
        // Safety: never leave old default with empty classification
        if (newUseFor.length > 0 && currentDefaultContact.id) {
          editContact(currentDefaultContact.id, {
            useFor: newUseFor,
            isDefault: false,
          })
        }
      }

      editContact(currentContact.id, {
        name: data.name,
        emails: data.emails,
        address: data.address ?? null,
        description: data.description ?? null,
        cityId: data.cityId ?? null,
        neighborhoodId: data.neighborhoodId ?? null,
        useFor,
        phones: data.phones ?? [],
        socialNetworks: data.socialNetworks ?? [],
        isDefault: useFor.includes(DEFAULT_VALUE),
      })
    }
    handleClose()
  }, [recordId, contacts, pendingDeletes, currentDefaultContact, addContact, editContact, handleClose])

  // Delete handler — ALL deletes trigger confirmation dialog
  const handleDelete = useCallback((id: string) => {
    const contact = contacts.find((c) => c.id === id)
    if (!contact) return

    setPendingDeleteId(id)
    setDeleteDialogOpen(true)
  }, [contacts])

  const handleDeleteConfirm = useCallback(() => {
    if (pendingDeleteId) {
      deleteContact(pendingDeleteId)
    }
    setPendingDeleteId(null)
    setDeleteDialogOpen(false)
  }, [pendingDeleteId, deleteContact])

  const handleDeleteCancel = useCallback(() => {
    setPendingDeleteId(null)
    setDeleteDialogOpen(false)
  }, [])

  // Compute names for the delete confirmation dialog
  const deleteDialogContact = pendingDeleteId ? contacts.find((c) => c.id === pendingDeleteId) : null
  const deleteDialogNewDefault = pendingDeleteId
    ? contacts.find((c) => c.id !== pendingDeleteId && !pendingDeletes.has(c.id ?? ''))
    : null

  return (
    <>
      <ContactGrid
        recordId={recordId}
        mode={mode}
        useForOptions={normalizedUseForOptions}
        phoneCategoryOptions={resolvedPhoneCategoryOptions}
        socialNetworkTypes={socialNetworkTypes}
        isLoading={isLoading}
        onAddContact={handleAdd}
        onEditContact={handleEdit}
        onViewContact={handleView}
        onDeleteContact={handleDelete}
        onRestoreContact={restoreContact}
      />
      <DeleteDefaultConfirmationDialog
        open={deleteDialogOpen}
        isDefaultDelete={!!deleteDialogContact?.useFor.includes(DEFAULT_VALUE) && !!deleteDialogNewDefault}
        defaultContactName={deleteDialogContact?.name ?? ''}
        newDefaultName={deleteDialogNewDefault?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
      <ContactModal
        open={modalState.isOpen}
        mode={modalState.mode}
        contact={modalState.contact}
        lookupConfig={adapter.lookupConfig}
        useForOptions={normalizedUseForOptions}
        phoneCategoryOptions={resolvedPhoneCategoryOptions}
        isFirstContact={isFirstContact}
        currentDefaultContactName={
          currentDefaultContact && currentDefaultContact.id !== modalState.contact?.id
            ? currentDefaultContact.name
            : undefined
        }
        isOnlyDefault={isOnlyDefault}
        currentDefaultOnlyHasDefault={currentDefaultOnlyHasDefault}
        onSave={handleSave}
        onClose={handleClose}
      />
    </>
  )
}
